import PropTypes from 'prop-types';
import clsx from 'clsx';

//hooks
import { useTransformStringOnOffset } from '@/utils/hooks/format/useTransformString';

// Lib
import { TW_POST_TYPES } from '@/lib/posts/postTypes';
import { POST } from '@/lib/posts/postTypes';
import { TWITTER } from '@/lib/socialProfile';

// Components
import Carousel from '@/components/base/Carousel';
import Icon from '@/components/base/Icon';
import { Item } from '@/components/composite/MediaViewer/PostPreview/Item';
import { RetweetBadge } from '@/components/composite/Post/Twitter/RetweetBadge';
import { QuoteBadge } from '@/components/composite/Post/Twitter/QuoteBadge';
import { ReplyBadge } from '@/components/composite/Post/Twitter/ReplyBadge';

export const PreviewTwitter = ({ post, isLoading, ...restProps }) => {
    if (isLoading) {
        return (
            <span className="square block animate-pulse rounded-md bg-icoGray-300 dark:bg-icoDarkMode-deepBlack" />
        );
    }

    //needed since links and polls are returned in the medias
    const carouselMedia =
        post?.type === TW_POST_TYPES.TYPE_CAROUSEL &&
        post?.medias?.filter((media) => media?.type === 'image');

    return (
        <div
            className="group relative flex min-h-[400px] w-full flex-col overflow-hidden rounded-md bg-icoGray-100 p-16 dark:bg-icoDarkMode-deepBlack"
            aria-label="preview-twitter">
            {post?.type === TW_POST_TYPES.TYPE_RETWEET && (
                <>
                    <div className="mb-2 space-y-5">
                        <p className="text-xs">
                            <RetweetBadge />
                        </p>
                        <InitialTweetAuthor post={post} />
                    </div>
                    <Status
                        caption={post?.initial_tweet?.caption_formatted}
                        tags={post?.initial_tweet?.tags}
                        className={clsx('font-GMedium text-lg', {
                            'mb-8': post?.type !== TW_POST_TYPES.TYPE_STATUS
                        })}
                    />
                </>
            )}
            {post?.type === TW_POST_TYPES.TYPE_QUOTE && (
                <>
                    <p className="mb-5 text-xs">
                        <QuoteBadge />
                    </p>
                    <Status
                        caption={post?.caption_formatted}
                        tags={post?.tags}
                        className={'mb-2 font-GMedium text-lg'}
                    />
                    <div
                        className={clsx(
                            'space-y-2 rounded-md bg-icoGray-300 p-m dark:bg-icoDarkMode-anthracite',
                            { 'mb-8': post?.type !== TW_POST_TYPES.TYPE_STATUS }
                        )}>
                        <InitialTweetAuthor post={post} />
                        <Status
                            caption={post?.initial_tweet?.caption_formatted}
                            tags={post?.initial_tweet?.tags}
                            className={'font-GMedium text-xs'}
                        />
                    </div>
                </>
            )}

            {post?.type === TW_POST_TYPES.TYPE_REPLY && (
                <>
                    <div className="mb-2">
                        <p className="mb-5 text-xs">
                            <ReplyBadge />
                        </p>
                        <InitialTweetAuthor post={post} />
                    </div>
                    <Status
                        caption={post?.caption_formatted}
                        tags={post?.tags}
                        className={clsx('font-GMedium text-lg', {
                            'mb-8': post?.type !== TW_POST_TYPES.TYPE_STATUS
                        })}
                    />
                </>
            )}

            {post?.type !== TW_POST_TYPES.TYPE_REPLY &&
                post?.type !== TW_POST_TYPES.TYPE_QUOTE &&
                post?.type !== TW_POST_TYPES.TYPE_RETWEET && (
                    <Status
                        aria-label="twitter-status"
                        caption={post?.caption_formatted}
                        tags={post?.tags}
                        className={clsx('font-GMedium text-xl', {
                            'mb-8': post?.type !== TW_POST_TYPES.TYPE_STATUS
                        })}
                    />
                )}

            {post?.sub_type === TW_POST_TYPES.TYPE_POLL && post?.medias[0]?.poll?.options && (
                <div className="mt-5 flex flex-col space-y-5 text-base" aria-label="twitter-poll">
                    {post?.medias[0]?.poll?.options?.map((option, index) => (
                        <div key={index} className="flex items-center justify-between">
                            <span>{option?.label}</span>
                            {(option?.percentage || option?.percentage === 0) && (
                                <span>{option?.percentage}%</span>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {(post?.type === TW_POST_TYPES.TYPE_LINK ||
                post?.sub_type === TW_POST_TYPES.TYPE_LINK) && (
                <a
                    href={post?.medias[0]?.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex min-w-150 items-center justify-between overflow-hidden rounded border border-icoGray-300 bg-white p-5 dark:border-icoDarkMode-beton dark:bg-icoDarkMode-anthracite"
                    aria-label="twitter-link">
                    <div className="flex flex-col space-y-1">
                        <span className="text-xxs">{post?.medias[0]?.text}</span>
                        <p className="flex items-center text-icoGray-500 dark:text-icoDarkMode-wolf">
                            <Icon icon="link" className="text-xxs" />
                            <span className="ml-2 text-xxs">
                                {post?.medias[0]?.link?.length > 30
                                    ? `${post?.medias[0]?.link.substring(0, 30)}...`
                                    : post?.medias[0]?.link}
                            </span>
                        </p>
                    </div>
                    <Icon
                        icon="chevron-right"
                        className="text-xs text-icoGray-500 dark:text-icoDarkMode-wolf"
                    />
                </a>
            )}
            {(post?.type === TW_POST_TYPES.TYPE_PHOTO ||
                post?.sub_type === TW_POST_TYPES.TYPE_PHOTO ||
                post?.type === TW_POST_TYPES.TYPE_VIDEO ||
                post?.sub_type === TW_POST_TYPES.TYPE_VIDEO) && (
                <Item
                    post={post}
                    postType={POST}
                    mediaId={0}
                    media={post?.medias[0]}
                    config={{ withAltText: false, imgFit: 'cover' }}
                    aria-label="twitter-media"
                    network={TWITTER}
                    {...restProps}
                />
            )}

            {(post?.type === TW_POST_TYPES.TYPE_CAROUSEL ||
                post?.subtype === TW_POST_TYPES.TYPE_CAROUSEL) && (
                <Carousel
                    config={{ slidesToScroll: 1 }}
                    hasDots
                    navPosition="external"
                    dotVariant="darken"
                    navOnHover={true}
                    aria-label="twitter-carousel">
                    {carouselMedia?.map((media, index) => {
                        return (
                            <Carousel.Slide key={index} size="full">
                                <Item
                                    post={post}
                                    postType={POST}
                                    mediaId={index}
                                    media={media}
                                    config={{ withAltText: false, imgFit: 'cover' }}
                                    network={TWITTER}
                                    {...restProps}
                                />
                            </Carousel.Slide>
                        );
                    })}
                </Carousel>
            )}
        </div>
    );
};

PreviewTwitter.propTypes = {
    post: PropTypes.object,
    isLoading: PropTypes.bool,
    className: PropTypes.string
};

const InitialTweetAuthor = ({ post }) => {
    return (
        <div className="flex space-x-1 text-xs">
            <span className="font-GBold">{post?.initial_author?.slug}</span>
            <a
                href={post?.initial_author?.permalink}
                rel="noopener noreferrer"
                target="_blank"
                className="text-icoBlue">
                {post?.initial_author?.tag}
            </a>
        </div>
    );
};

const Status = ({ caption, tags, className, ...restProps }) => {
    const { OffsetReplace } = useTransformStringOnOffset();

    if (!caption) return null;

    return (
        <div className={className} {...restProps}>
            <OffsetReplace
                text={caption}
                components={Object.assign(
                    // eslint-disable-next-line jsx-a11y/anchor-has-content
                    { p: <p />, br: <br /> },
                    tags?.map((tag, id) => (
                        // eslint-disable-next-line jsx-a11y/anchor-has-content
                        <a
                            key={id}
                            href={tag.link}
                            className="text-icoBlue"
                            target="_blank"
                            rel="noopener noreferrer"
                        />
                    ))
                )}
            />
        </div>
    );
};

Status.propTypes = {
    caption: PropTypes.string,
    tags: PropTypes.array,
    className: PropTypes.string
};

export default Status;
